import { NotificationType, UserReminderType } from "@prisma/client";
import { DateTime } from "luxon";

export const NOTIFICATION_COLORS: Record<NotificationType, string> = {
    [NotificationType.EVENT_ANNOUNCEMENT]: "#9BE1E3",
    [NotificationType.EVENT_CANCELED]: "#0075FF",
    [NotificationType.EVENT_PROPOSAL]: "#602EF4",
    [NotificationType.HOST_APPLICATION]: "#FBA599",
    [NotificationType.ATTENDEE_PENDING]: "#FFD233",
    [NotificationType.MESSAGE_FROM_HOST]: "#4FE6B0",
    [NotificationType.MESSAGE_FROM_COMMUNITY_MANAGER]: "#99DB9D",
    [NotificationType.MESSAGE_IN_EVENT_CONVERSATION]: "#9BE1E3",
    [NotificationType.REIMBURSEMENT_UPDATE]: "#0075FF",
    [NotificationType.SPONSOR_APPLICATION]: "#602EF4",
    [NotificationType.FEEDBACK_REQUEST]: "#4FE6B0",
    [NotificationType.FEEDBACK_AVAILABLE]: "#99DB9D",
    [NotificationType.INACTIVE_HOST]: "#9BE1E3",
    [NotificationType.INVITE_TO_CONNECT]: "#0075FF",
};

export const NOTIFICATION_ICONS: Record<NotificationType, string> = {
    [NotificationType.EVENT_ANNOUNCEMENT]: "🎉",
    [NotificationType.EVENT_CANCELED]: "📋",
    [NotificationType.EVENT_PROPOSAL]: "🗓️",
    [NotificationType.HOST_APPLICATION]: "📆",
    [NotificationType.ATTENDEE_PENDING]: "✅",
    [NotificationType.MESSAGE_FROM_HOST]: "💬",
    [NotificationType.MESSAGE_FROM_COMMUNITY_MANAGER]: "💬",
    [NotificationType.MESSAGE_IN_EVENT_CONVERSATION]: "💬",
    [NotificationType.REIMBURSEMENT_UPDATE]: "💰",
    [NotificationType.FEEDBACK_REQUEST]: "💬",
    [NotificationType.FEEDBACK_AVAILABLE]: "�",
    [NotificationType.INACTIVE_HOST]: "👥",
    [NotificationType.INVITE_TO_CONNECT]: "👥",
    [NotificationType.SPONSOR_APPLICATION]: "💰",
};

export const NOTIFICATION_TITLES: Record<NotificationType, string> = {
    [NotificationType.EVENT_ANNOUNCEMENT]: "Event announcement",
    [NotificationType.EVENT_CANCELED]: "Event canceled",
    [NotificationType.EVENT_PROPOSAL]: "Event proposal",
    [NotificationType.HOST_APPLICATION]: "Host application",
    [NotificationType.ATTENDEE_PENDING]: "Attendee pending",
    [NotificationType.MESSAGE_FROM_HOST]: "Message from host",
    [NotificationType.MESSAGE_FROM_COMMUNITY_MANAGER]:
        "Message from community manager",
    [NotificationType.MESSAGE_IN_EVENT_CONVERSATION]:
        "Message in event conversation",
    [NotificationType.REIMBURSEMENT_UPDATE]: "Reimbursement update",
    [NotificationType.SPONSOR_APPLICATION]: "Sponsor application",
    [NotificationType.FEEDBACK_REQUEST]: "Feedback request",
    [NotificationType.FEEDBACK_AVAILABLE]: "Feedback available",
    [NotificationType.INACTIVE_HOST]: "Inactive host",
    [NotificationType.INVITE_TO_CONNECT]: "Invite to connect",
};

export const REMINDER_EXPIRATION: Record<
    UserReminderType,
    (event?: { startTime: Date }) => Date
> = {
    // For approval, expire 1 hour before event start or 7 days if no start time
    [UserReminderType.APPROVE_GUESTS]: (event) =>
        event?.startTime
            ? DateTime.fromJSDate(event.startTime)
                  .minus({ hours: 1 })
                  .toJSDate()
            : DateTime.now().plus({ days: 7 }).toJSDate(),

    // For waitlist, expire 1 hour before event start or 7 days if no start time
    [UserReminderType.MANAGE_WAITLIST]: (event) =>
        event?.startTime
            ? DateTime.fromJSDate(event.startTime)
                  .minus({ hours: 1 })
                  .toJSDate()
            : DateTime.now().plus({ days: 7 }).toJSDate(),

    // For checkins, expire 2 hours after event start
    [UserReminderType.MANAGE_CHECKINS]: (event) =>
        event?.startTime
            ? DateTime.fromJSDate(event.startTime).plus({ hours: 2 }).toJSDate()
            : DateTime.now().plus({ hours: 4 }).toJSDate(),

    // For today's event, expire at event start time
    [UserReminderType.EVENT_TODAY]: (event) =>
        event?.startTime
            ? event.startTime
            : DateTime.now().endOf("day").toJSDate(),

    // For tomorrow's event, expire at end of tomorrow
    [UserReminderType.EVENT_TOMORROW]: (event) =>
        DateTime.now().plus({ days: 1 }).endOf("day").toJSDate(),

    // For draft events, expire after 7 days
    [UserReminderType.EVENT_IN_DRAFT]: () =>
        DateTime.now().plus({ days: 7 }).toJSDate(),

    // For custom reminders, default to 7 days
    [UserReminderType.CUSTOM]: () =>
        DateTime.now().plus({ days: 7 }).toJSDate(),
};
